import React from 'react';
import { Helmet } from 'react-helmet-async';
import BlogCard from '../components/BlogCard';
import { blogPosts } from '../utils/constants';

const Blog = () => {
    // Ensure blogPosts is defined and is an array
    const posts = blogPosts && Array.isArray(blogPosts) ? blogPosts : [];

    return (
        <>
            <Helmet>
                <title>Blog & Insights | LAMSAT Engineering</title>
                <meta
                    name="description"
                    content="Latest insights, tips, and news about engineering services, construction trends, and project management in Dubai."
                />
            </Helmet>

            {/* Hero Section - Fixed for all device sizes */}
            <section className="bg-primary-blue" style={{ 
                paddingTop: 'calc(80px + 2rem)',
                paddingBottom: '2rem'
            }}>
                <div className="container">
                    <div className="text-center text-white py-3 py-md-4">
                        <h1 className="fw-bold mb-3" style={{
                            fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
                            lineHeight: '1.2',
                            marginTop: '0.5rem'
                        }}>
                            Blog & Insights
                        </h1>
                        <p className="lead mb-0" style={{ 
                            color: 'rgba(255, 255, 255, 0.9)',
                            fontSize: 'clamp(1rem, 2.2vw, 1.25rem)',
                            lineHeight: '1.5',
                            padding: '0 0.75rem'
                        }}>
                            Latest updates, tips, and industry insights from our experts
                        </p>
                    </div>
                </div>
            </section>

            {/* Blog Posts */}
            <section className="py-4 py-lg-5">
                <div className="container">
                    {posts.length > 0 ? (
                        <div className="row g-4">
                            {posts.map((blog, index) => (
                                <BlogCard key={blog.id || index} blog={blog} index={index} />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-5">
                            <i className="fas fa-newspaper fa-3x text-muted mb-4"></i>
                            <h3 className="mb-3">No Blog Posts Available</h3>
                            <p className="text-muted">Check back soon for updates!</p>
                        </div>
                    )}
                </div>
            </section>

            {/* Newsletter */}
            <section className="py-4 py-lg-5 bg-off-white">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-6 mb-4 mb-lg-0">
                            <h3 className="mb-3" style={{
                                fontSize: 'clamp(1.3rem, 2.8vw, 1.5rem)'
                            }}>Stay Updated</h3>
                            <p className="text-muted mb-4 mb-lg-0" style={{
                                fontSize: 'clamp(0.95rem, 1.8vw, 1.05rem)',
                                lineHeight: '1.6'
                            }}>
                                Subscribe to our newsletter for the latest engineering insights,
                                project updates, and exclusive offers.
                            </p>
                        </div>
                        <div className="col-lg-6">
                            <div className="d-flex flex-column flex-sm-row gap-2">
                                <input
                                    type="email"
                                    className="form-control form-control-lg"
                                    placeholder="Enter your email address"
                                    style={{
                                        fontSize: 'clamp(0.95rem, 1.6vw, 1rem)',
                                        padding: '0.75rem 1rem',
                                        minHeight: '50px'
                                    }}
                                />
                                <button 
                                    className="btn btn-primary-custom px-4" 
                                    type="button"
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        fontSize: 'clamp(0.95rem, 1.6vw, 1rem)',
                                        whiteSpace: 'nowrap',
                                        minHeight: '50px'
                                    }}
                                >
                                    Subscribe
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Device-specific CSS fixes */}
            <style jsx>{`
                /* Base styles */
                section:first-of-type {
                    position: relative;
                    z-index: 1;
                    margin-top: 0 !important;
                }
                
                /* Extra Small devices (phones, less than 576px) */
                @media (max-width: 575.98px) {
                    section:first-of-type {
                        padding-top: calc(60px + 1.5rem) !important;
                        padding-bottom: 1.5rem !important;
                    }
                    
                    section:first-of-type h1 {
                        margin-top: 0.25rem !important;
                        font-size: 1.8rem !important;
                    }
                    
                    section:first-of-type p {
                        padding: 0 0.5rem !important;
                        font-size: 1rem !important;
                    }
                }
                
                /* Small devices (landscape phones, 576px to 767.98px) */
                @media (min-width: 576px) and (max-width: 767.98px) {
                    section:first-of-type {
                        padding-top: calc(65px + 1.75rem) !important;
                        padding-bottom: 1.75rem !important;
                    }
                    
                    section:first-of-type h1 {
                        margin-top: 0.5rem !important;
                        font-size: 2rem !important;
                    }
                    
                    section:first-of-type p {
                        padding: 0 1rem !important;
                        font-size: 1.1rem !important;
                    }
                }
                
                /* Medium devices (tablets, 768px to 991.98px) */
                @media (min-width: 768px) and (max-width: 991.98px) {
                    section:first-of-type {
                        padding-top: calc(70px + 2rem) !important;
                        padding-bottom: 2rem !important;
                    }
                    
                    section:first-of-type h1 {
                        margin-top: 0.75rem !important;
                        font-size: 2.3rem !important;
                    }
                    
                    section:first-of-type p {
                        padding: 0 2rem !important;
                        font-size: 1.2rem !important;
                        max-width: 80%;
                        margin-left: auto;
                        margin-right: auto;
                    }
                    
                    /* Blog posts section for medium */
                    section:nth-of-type(2) {
                        padding-top: 2rem !important;
                        padding-bottom: 2rem !important;
                    }
                    
                    /* Newsletter section for medium */
                    section:nth-of-type(3) {
                        padding-top: 2rem !important;
                        padding-bottom: 2rem !important;
                    }
                }
                
                /* Large devices (desktops, 992px to 1199.98px) */
                @media (min-width: 992px) and (max-width: 1199.98px) {
                    section:first-of-type {
                        padding-top: calc(75px + 2.25rem) !important;
                        padding-bottom: 2.25rem !important;
                    }
                    
                    section:first-of-type h1 {
                        margin-top: 1rem !important;
                        font-size: 2.5rem !important;
                    }
                    
                    section:first-of-type p {
                        padding: 0 3rem !important;
                        font-size: 1.25rem !important;
                        max-width: 70%;
                        margin-left: auto;
                        margin-right: auto;
                    }
                }
                
                /* Extra large devices (large desktops, 1200px and up) */
                @media (min-width: 1200px) {
                    section:first-of-type {
                        padding-top: calc(80px + 2.5rem) !important;
                        padding-bottom: 2.5rem !important;
                    }
                    
                    section:first-of-type h1 {
                        margin-top: 1rem !important;
                        font-size: 2.8rem !important;
                    }
                    
                    section:first-of-type p {
                        padding: 0 4rem !important;
                        font-size: 1.35rem !important;
                        max-width: 60%;
                        margin-left: auto;
                        margin-right: auto;
                    }
                }
                
                /* Ensure container content is properly positioned */
                .container {
                    position: relative;
                    z-index: 2;
                }
                
                /* Newsletter form responsive adjustments */
                @media (max-width: 767.98px) {
                    .d-flex.flex-column.flex-sm-row {
                        flex-direction: column !important;
                    }
                    
                    .d-flex.flex-column.flex-sm-row input,
                    .d-flex.flex-column.flex-sm-row button {
                        width: 100% !important;
                        margin-bottom: 0.5rem;
                    }
                }
                
                @media (min-width: 768px) and (max-width: 991.98px) {
                    .d-flex.flex-column.flex-sm-row {
                        flex-direction: row !important;
                    }
                    
                    .d-flex.flex-column.flex-sm-row input {
                        flex: 1;
                        min-width: 250px;
                    }
                    
                    .d-flex.flex-column.flex-sm-row button {
                        flex-shrink: 0;
                    }
                }
            `}</style>
        </>
    );
};

export default Blog;